import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { pushSettings } from '@bb/contracts';
import { Badge, Button, Checkbox, ConfirmDialog, EmptyState, ErrorState, Field, Input, Modal, Select, Textarea } from '@bb/ui';
import { ApiError, type AndroidChannelBody, type AndroidChannelView, type AppInstanceSummary, type UpdateAndroidChannelBody } from '../../../api/client.ts';
import { CHANNEL_COVERAGE_WARNING_PCT } from '../../composer/push/PushSettingsTab.tsx';
import { contractIssues, problemFieldErrors, problemText } from './problems.ts';
import { pushKeys, useApi, type PushSettingsPageData } from './usePushSettingsData.ts';

type Importance = AndroidChannelBody['importance'];
const IMPORTANCE: Array<{ value: Importance; label: string }> = [
  { value: 'min', label: 'Min' },
  { value: 'low', label: 'Low' },
  { value: 'default', label: 'Default' },
  { value: 'high', label: 'High' },
];
const IMPORTANCE_LABEL = Object.fromEntries(IMPORTANCE.map((i) => [i.value, i.label])) as Record<Importance, string>;
const MESSAGES: Record<string, string> = {
  channelId: 'Lowercase letters, digits and _ (up to 64).',
  name: 'Enter a name of up to 80 characters.',
  description: 'At most 300 characters.',
};

/** The channel as the dialog edits it; `id` is null for a new one. */
interface Draft {
  id: string | null;
  appInstanceId: string;
  channelId: string;
  name: string;
  description: string;
  importance: Importance;
  isDefault: boolean;
  isFallback: boolean;
  /** Whether the stored channel had a description (so clearing it is sent). */
  hadDescription: boolean;
}

const newDraft = (): Draft => ({ id: null, appInstanceId: '', channelId: '', name: '', description: '', importance: 'default', isDefault: false, isFallback: false, hadDescription: false });
const draftOf = (c: AndroidChannelView): Draft => ({
  id: c.id,
  appInstanceId: c.appInstanceId ?? '',
  channelId: c.channelId,
  name: c.name,
  description: c.description ?? '',
  importance: c.importance,
  isDefault: c.isDefault,
  isFallback: c.isFallback,
  hadDescription: Boolean(c.description),
});

/** The body to send (create or update) or one message per field; the app id is not part of the contract check (it comes from the list). */
function checkChannel(d: Draft): { create: AndroidChannelBody | null; update: UpdateAndroidChannelBody | null; issues: Record<string, string> } {
  const description = d.description.trim();
  const fields = { name: d.name, importance: d.importance, isDefault: d.isDefault, isFallback: d.isFallback, ...(description || d.hadDescription ? { description } : {}) };
  if (d.id) {
    const parsed = pushSettings.UpdateAndroidChannelBody.safeParse(fields);
    return parsed.success ? { create: null, update: parsed.data, issues: {} } : { create: null, update: null, issues: contractIssues(parsed.error.issues, MESSAGES) };
  }
  const parsed = pushSettings.AndroidChannelBody.safeParse({ ...fields, appInstanceId: null, channelId: d.channelId });
  if (!parsed.success) return { create: null, update: null, issues: contractIssues(parsed.error.issues, MESSAGES) };
  return { create: { ...parsed.data, appInstanceId: d.appInstanceId || null }, update: null, issues: {} };
}

/** A 409 names no field: it is the fallback when this channel claims it, else the channel id. */
function conflictIssues(error: unknown, draft: Draft): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  if (error.status === 409) return draft.isFallback ? { isFallback: 'Another channel is already the fallback. Clear it there first.' } : { channelId: 'This channel ID is already in the registry for these apps.' };
  if (error.code === 'push_platform_mismatch') return { appInstanceId: 'Choose an Android app.' };
  return problemFieldErrors(error);
}

const coverageOf = (pct: number | null) => (pct === null ? null : Math.round(pct));

/**
 * The Android notification channels the apps create (BB-77): id, name, importance, which apps, default and fallback
 * (one per environment), and how many active installations reported the channel. Channels are archived, not deleted.
 */
export function AndroidChannelsTable({ environmentId, data, canEdit }: { environmentId: string; data: PushSettingsPageData; canEdit: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [archiving, setArchiving] = useState<AndroidChannelView | null>(null);
  const refresh = async () => {
    void queryClient.invalidateQueries({ queryKey: pushKeys.sdkConfigs(environmentId) });
    await queryClient.invalidateQueries({ queryKey: pushKeys.channels(environmentId) });
  };
  const save = useMutation({
    mutationFn: async (d: Draft) => {
      const checked = checkChannel(d);
      if (d.id && checked.update) return api.updatePushAndroidChannel(environmentId, d.id, checked.update);
      if (checked.create) return api.createPushAndroidChannel(environmentId, checked.create);
      throw new Error('The channel is not valid.');
    },
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const archive = useMutation({
    mutationFn: (id: string) => api.archivePushAndroidChannel(environmentId, id),
    onSuccess: async () => {
      setArchiving(null);
      await refresh();
    },
  });
  const androidApps = data.appsWithPush.filter((a) => a.platform === 'android');
  const appName = (id: string | null) => (id ? (data.apps.data?.find((a) => a.id === id)?.name ?? 'An app that no longer exists') : 'All Android apps');

  if (data.channels.isError) return <ErrorState title="Android channels could not be loaded" message={problemText(data.channels.error)} onRetry={() => void data.channels.refetch()} />;
  if (!data.channels.data) return <p aria-busy="true">Loading channels…</p>;
  const all = data.channels.data;
  const active = all.filter((c) => !c.archivedAt);
  const archivedCount = all.length - active.length;
  const rows = showArchived ? [...active, ...all.filter((c) => c.archivedAt)] : active;
  const low = active.filter((c) => c.seenOnInstallationsPct !== null && c.seenOnInstallationsPct < CHANNEL_COVERAGE_WARNING_PCT).map((c) => c.channelId);
  const unknown = active.some((c) => c.seenOnInstallationsPct === null);
  const fallback = active.find((c) => c.isFallback);
  const open = (draft: Draft) => {
    save.reset();
    setEditing(draft);
  };

  return (
    <>
      <p className="hint">
        Channels are created by the app's code; this registry lists them for the composer and the generated config. A push that names a channel the device does not have shows in the fallback channel.
      </p>
      {low.length > 0 && (
        <p className="notice notice--warning" role="status">
          {low.join(', ')} {low.length === 1 ? 'is' : 'are'} missing on more than {100 - CHANNEL_COVERAGE_WARNING_PCT}% of active Android installations: those devices show {low.length === 1 ? 'its' : 'their'} pushes in the fallback channel.
        </p>
      )}
      {unknown && <p className="hint">Coverage appears once the apps report their channels (Android SDK); until then it is not known.</p>}
      <div className="actions">
        {canEdit && (
          <Button variant="primary" onClick={() => open(newDraft())}>
            Add channel
          </Button>
        )}
        {archivedCount > 0 && <Checkbox label={`Show archived (${archivedCount})`} checked={showArchived} onCheckedChange={setShowArchived} />}
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No Android channels" description="Pushes go to the default channel the app creates. Add the channels the app's code creates to choose them in messages." />
      ) : (
        <div className="push-table-scroll">
          <table className="table" aria-label="Android channels">
            <thead>
              <tr>
                <th scope="col">Channel ID</th>
                <th scope="col">Name</th>
                <th scope="col">Importance</th>
                <th scope="col">Apps</th>
                <th scope="col">Coverage</th>
                <th scope="col">Status</th>
                {canEdit && (
                  <th scope="col">
                    <span className="bb-visually-hidden">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const pct = coverageOf(c.seenOnInstallationsPct);
                return (
                  <tr key={c.id} className={c.archivedAt ? 'push-row--archived' : undefined}>
                    <td>
                      <code>{c.channelId}</code>
                    </td>
                    <td>
                      {c.name} {c.isDefault && <Badge tone="info">Default</Badge>} {c.isFallback && <Badge tone="info">Fallback</Badge>}
                      {c.description && <div className="hint">{c.description}</div>}
                    </td>
                    <td>{IMPORTANCE_LABEL[c.importance]}</td>
                    <td>{appName(c.appInstanceId)}</td>
                    <td>
                      {pct === null ? (
                        <span className="hint">Not reported yet</span>
                      ) : (
                        <>
                          {pct}% {c.seenOnInstallationsPct! < CHANNEL_COVERAGE_WARNING_PCT && !c.archivedAt && <Badge tone="warning">Below {CHANNEL_COVERAGE_WARNING_PCT}%</Badge>}
                        </>
                      )}
                    </td>
                    <td>{c.archivedAt ? 'Archived' : 'Active'}</td>
                    {canEdit && (
                      <td>
                        {!c.archivedAt && (
                          <>
                            <Button variant="ghost" size="sm" aria-label={`Edit ${c.channelId}`} onClick={() => open(draftOf(c))}>
                              Edit
                            </Button>
                            <Button variant="ghost" size="sm" aria-label={`Archive ${c.channelId}`} onClick={() => setArchiving(c)}>
                              Archive
                            </Button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <ChannelDialog
          draft={editing}
          androidApps={androidApps}
          fallback={fallback && fallback.id !== editing.id ? fallback : undefined}
          busy={save.isPending}
          error={save.error}
          onChange={(patch) => {
            save.reset();
            setEditing((d) => (d ? { ...d, ...patch } : d));
          }}
          onCancel={() => setEditing(null)}
          onSave={() => save.mutate(editing)}
        />
      )}
      <ConfirmDialog open={archiving !== null} onOpenChange={(o) => !o && setArchiving(null)} title={`Archive ${archiving?.channelId ?? ''}?`} confirmLabel="Archive" busy={archive.isPending} onConfirm={() => archiving && archive.mutate(archiving.id)}>
        It leaves the registry and the generated config{archiving?.isFallback ? ' and stops being the fallback' : ''}. Messages that still name it show in the app's default channel on devices; the app keeps the channel until its code deletes it.
        {archive.isError && <span role="alert"> {problemText(archive.error)}</span>}
      </ConfirmDialog>
    </>
  );
}

function ChannelDialog({ draft, androidApps, fallback, busy, error, onChange, onCancel, onSave }: {
  draft: Draft;
  androidApps: AppInstanceSummary[];
  fallback: AndroidChannelView | undefined;
  busy: boolean;
  error: unknown;
  onChange: (patch: Partial<Draft>) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const checked = checkChannel(draft);
  const server = conflictIssues(error, draft);
  const issue = (key: string) => checked.issues[key] ?? server[key];
  const valid = draft.id ? checked.update !== null : checked.create !== null;
  const unmapped = error != null && Object.keys(server).length === 0 ? problemText(error) : null;
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onCancel()}
      title={draft.id ? `Edit ${draft.channelId}` : 'Add Android channel'}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={onSave}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack-form">
        {draft.id ? (
          <p>
            Channel ID <code>{draft.channelId}</code> · {draft.appInstanceId ? androidApps.find((a) => a.id === draft.appInstanceId)?.name : 'All Android apps'}
          </p>
        ) : (
          <>
            <Field label="Channel ID" hint="Exactly as the app's code creates it; it cannot be changed later." error={issue('channelId')}>
              <Input value={draft.channelId} spellCheck={false} placeholder="trading_alerts" onChange={(e) => onChange({ channelId: e.target.value })} />
            </Field>
            <Field label="Apps" error={issue('appInstanceId')}>
              <Select options={[{ value: '', label: 'All Android apps' }, ...androidApps.map((a) => ({ value: a.id, label: a.name }))]} value={draft.appInstanceId} onChange={(e) => onChange({ appInstanceId: e.target.value })} />
            </Field>
          </>
        )}
        <Field label="Name" hint="For people choosing it in the composer; the name on devices comes from the app." error={issue('name')}>
          <Input value={draft.name} onChange={(e) => onChange({ name: e.target.value })} />
        </Field>
        <Field label="Description" error={issue('description')}>
          <Textarea value={draft.description} rows={2} onChange={(e) => onChange({ description: e.target.value })} />
        </Field>
        <Field label="Importance" hint="High makes a sound and peeks on screen; min shows no icon in the status bar.">
          <Select options={IMPORTANCE} value={draft.importance} onChange={(e) => onChange({ importance: e.target.value as Importance })} />
        </Field>
        <Checkbox label="Default channel" checked={draft.isDefault} onCheckedChange={(isDefault) => onChange({ isDefault })} />
        <Checkbox label="Fallback channel" checked={draft.isFallback} onCheckedChange={(isFallback) => onChange({ isFallback })} />
        <p className="hint">The fallback takes pushes that name no channel or one the device does not have; one per environment.</p>
        {draft.isFallback && fallback && <p className="hint">{fallback.channelId} is the fallback now.</p>}
        {issue('isFallback') && (
          <p className="bb-field__error" role="alert">
            {issue('isFallback')}
          </p>
        )}
        {unmapped && <p role="alert">{unmapped}</p>}
      </div>
    </Modal>
  );
}
